/**
 * Drag the Words composite grading — FITB items only (drag mode).
 */

import type {
    ContentGrader,
    GradingResult,
    ScoringConfig,
    ValidationResult,
    ValidationError,
    QuestionCounter,
} from '@ai-content-hub/content-core';
import { fitbGrader, type FITBAnswer } from '@ai-content-hub/content-elements';
import type { QtDragTheWordsConfig, QtDragTheWordsData, QtDragTheWordsItem, QtDragTheWordsScoringConfig } from './schema';
import { normalizeQtDragTheWordsAnswerMap } from './schema';
import map from 'lodash/map';
import trim from 'lodash/trim';

export interface QtDragTheWordsItemResult {
    itemId: string;
    isCorrect: boolean;
    score: number;
    min: number;
    max: number;
    details?: Record<string, unknown>;
}

function isFitbAnswered(item: QtDragTheWordsItem, answer: unknown, allowPartial = false): boolean {
    const blanks = item.data.blanks ?? [];
    if (blanks.length === 0) return !allowPartial;
    if (answer == null || typeof answer !== 'object') return false;
    const f = answer as FITBAnswer;
    const filled = (b: { id: string }) => {
        const v = f[b.id];
        return typeof v === 'string' && trim(v) !== '';
    };
    // Host-forced submit: one filled blank is enough; empty blanks are graded wrong.
    return allowPartial ? blanks.some(filled) : blanks.every(filled);
}

export function validateQtDragTheWordsAnswer(
    data: QtDragTheWordsData,
    answer: unknown,
    opts?: { allowPartial?: boolean }
): boolean {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
        return false;
    }
    const rec = answer as Record<string, unknown>;
    const items = data.items ?? [];
    if (items.length === 0) {
        return false;
    }
    const normalized = normalizeQtDragTheWordsAnswerMap(data, rec);
    return items.some((item) => isFitbAnswered(item, normalized[item.id], opts?.allowPartial === true));
}

export function validateQtDragTheWordsContent(data: QtDragTheWordsData): ValidationResult {
    const errors: ValidationError[] = [];
    const items = data.items ?? [];
    if (items.length === 0) {
        errors.push({
            field: 'items',
            message: 'Add at least one drag-the-words exercise',
            code: 'required',
        });
    }
    for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx];
        const r = fitbGrader.validate!(item.data);
        if (!r.isValid) {
            for (const e of r.errors) {
                errors.push({
                    ...e,
                    field: `items.${idx}.${e.field}`,
                });
            }
        }
    }
    return { isValid: errors.length === 0, errors };
}

export function gradeQtDragTheWords(
    data: QtDragTheWordsData,
    answers: Record<string, unknown>,
    config?: QtDragTheWordsConfig,
): GradingResult {
    const normalizedAnswers = normalizeQtDragTheWordsAnswerMap(data, answers);
    const scoring = config?.scoring as (QtDragTheWordsScoringConfig & ScoringConfig) | undefined;
    const items = data.items ?? [];
    const pointsBase =
        scoring?.pointsPerQuestion ?? (scoring as { max?: number } | undefined)?.max ?? 1;
    const passingThreshold =
        scoring?.passingThreshold ??
        (scoring as { passingScore?: number } | undefined)?.passingScore ??
        0.7;
    const partialCredit = scoring?.partialCreditEnabled ?? true;

    const itemResults: QtDragTheWordsItemResult[] = map(items, (item) => {
        const raw = normalizedAnswers[item.id];
        const itemMax = item.points ?? pointsBase;
        const gradeResult = fitbGrader.grade(item.data, (raw as FITBAnswer) ?? {}, {
            max: itemMax,
            partialCredit,
        });
        return {
            itemId: item.id,
            isCorrect: gradeResult.isCorrect,
            score: gradeResult.score,
            min: 0,
            max: gradeResult.max,
            details: gradeResult.details,
        };
    });

    const totalScore = itemResults.reduce((sum, r) => sum + r.score, 0);
    const maxPoints = itemResults.reduce((sum, r) => sum + r.max, 0);
    const ratio = maxPoints > 0 ? totalScore / maxPoints : 0;
    const passed = ratio >= passingThreshold;

    let childCorrect = 0;
    let childTotal = 0;
    for (const item of items) {
        const r = itemResults.find((x) => x.itemId === item.id);
        const blanks = item.data.blanks ?? [];
        const n = blanks.length;
        childTotal += n;
        const d = r?.details as { perBlank?: { id: string; ok: boolean }[] } | undefined;
        const pb = d?.perBlank;
        if (Array.isArray(pb)) {
            for (const p of pb) {
                if (p.ok === true) {
                    childCorrect += 1;
                }
            }
        }
    }

    const questionCorrectCount = itemResults.filter((r) => r.isCorrect).length;

    return {
        isCorrect: passed,
        score: totalScore,
        max: maxPoints,
        details: {
            items: itemResults,
            passed,
            ratio,
            correctCount: childCorrect,
            totalItems: childTotal,
            questionCorrectCount,
            questionCount: itemResults.length,
        },
    };
}

export const qtDragTheWordsContentGrader: ContentGrader<QtDragTheWordsData, Record<string, unknown>> = {
    grade: (d, answer, cfg) =>
        gradeQtDragTheWords(d, (answer as Record<string, unknown>) ?? {}, cfg as QtDragTheWordsConfig),
    validateAnswer: (answer, d, opts) => validateQtDragTheWordsAnswer(d, answer, opts),
};

/**
 * Count gradable answer units (blanks) across all drag-the-words items.
 */
export function countDragTheWordsQuestions(data: QtDragTheWordsData): number {
    const items = data.items ?? [];
    let count = 0;
    for (const item of items) {
        count += (item.data.blanks ?? []).length;
    }
    return count;
}

export const qtDragTheWordsQuestionCounter: QuestionCounter<QtDragTheWordsData> = {
    countQuestions: countDragTheWordsQuestions,
};
